import type { Bar, StrategyProperties } from "@trader/backtester"
import type { ChartContext } from "lightweight-charts-indicators"
import type { RunFx } from "./accountCurrency.ts"
import type { CatalogItem, FxGapInfo, StrategyResult, WorkerRequest, WorkerResponse } from "./protocol.ts"

// The app's side of the backtest worker: one worker for every pane. Each pane has
// at most one run in the worker and one waiting behind it; a newer request
// replaces the waiting one, so the worker never spends time on superseded runs
// (and one pane's deep backtest can't queue up stale work for the others).
// Every request gets a reply, so no caller waits forever.

export type RunReply =
  | { readonly kind: "result"; readonly result: StrategyResult }
  | { readonly kind: "error"; readonly message: string; readonly fxGap?: FxGapInfo }
  /** A newer request for the same pane replaced this one before it ran. */
  | { readonly kind: "superseded" }

export interface RunRequest {
  readonly strategyId: string
  readonly bars: ReadonlyArray<Bar>
  readonly inputs: Record<string, unknown>
  readonly properties: Partial<StrategyProperties>
  readonly chart: ChartContext
  /** Rates to report in another currency than the symbol's. */
  readonly fx?: RunFx
}

interface Job {
  readonly run: number
  readonly request: RunRequest
  readonly resolve: (reply: RunReply) => void
}

let worker: Worker | null = null
let nextRun = 1
const inFlight = new Map<number, Job>()
const queued = new Map<number, Job>()

let catalog: Promise<ReadonlyArray<CatalogItem>> | null = null
let catalogRequest: { resolve: (items: ReadonlyArray<CatalogItem>) => void; reject: (error: Error) => void } | null = null

/** The worker died or never loaded: every caller gets the error, and the next call starts a new worker. */
const fail = (message: string): void => {
  worker?.terminate()
  worker = null
  const jobs = [...inFlight.values(), ...queued.values()]
  inFlight.clear()
  queued.clear()
  for (const job of jobs) job.resolve({ kind: "error", message })
  catalogRequest?.reject(new Error(message))
  catalogRequest = null
}

const onMessage = (event: MessageEvent<WorkerResponse>): void => {
  const message = event.data
  if (message.type === "catalog") {
    catalogRequest?.resolve(message.catalog)
    catalogRequest = null
    return
  }
  const job = inFlight.get(message.pane)
  if (job?.run !== message.run) return
  inFlight.delete(message.pane)
  const next = queued.get(message.pane)
  if (next !== undefined) {
    queued.delete(message.pane)
    start(message.pane, next)
  }
  // Delivered even when a newer request is waiting: the caller knows whether it's still current.
  job.resolve(
    message.type === "result"
      ? { kind: "result", result: message.result }
      : { kind: "error", message: message.message, ...(message.fxGap ? { fxGap: message.fxGap } : {}) }
  )
}

const workerInstance = (): Worker => {
  if (worker !== null) return worker
  const created = new Worker(new URL("./backtest.worker.ts", import.meta.url), { type: "module" })
  created.onmessage = onMessage
  // A failed script download fires a plain Event, without a message.
  created.onerror = (event: Event) =>
    fail(event instanceof ErrorEvent && event.message
      ? `The backtester stopped: ${event.message}`
      : "The backtester couldn't load. Reload the page.")
  created.onmessageerror = () => fail("The backtester sent a reply the page couldn't read.")
  worker = created
  return created
}

const send = (request: WorkerRequest): void => workerInstance().postMessage(request)

const start = (pane: number, job: Job): void => {
  inFlight.set(pane, job)
  try {
    send({ type: "run", run: job.run, pane, ...job.request })
  } catch (error) {
    inFlight.delete(pane)
    job.resolve({ kind: "error", message: error instanceof Error ? error.message : String(error) })
  }
}

/** Every strategy's id, name, and group (loads the worker on first call; retried after a failure). */
export const strategyCatalog = (): Promise<ReadonlyArray<CatalogItem>> => {
  if (catalog !== null) return catalog
  const loading = new Promise<ReadonlyArray<CatalogItem>>((resolve, reject) => {
    catalogRequest = { resolve, reject }
    send({ type: "catalog" })
  })
  loading.catch(() => {
    if (catalog === loading) catalog = null
  })
  catalog = loading
  return loading
}

/**
 * Backtests a strategy for a pane. Resolves with this run's reply, or with
 * "superseded" when a newer request for the same pane replaced it before it ran.
 */
export const runBacktest = (pane: number, request: RunRequest): Promise<RunReply> =>
  new Promise((resolve) => {
    const job: Job = { run: nextRun++, request, resolve }
    if (!inFlight.has(pane)) {
      start(pane, job)
      return
    }
    // The worker is busy with this pane: wait behind it, replacing an older waiting request.
    queued.get(pane)?.resolve({ kind: "superseded" })
    queued.set(pane, job)
  })
